import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { useAuthStore } from '@store/authStore';

const academyRegistrationSchema = z.object({
  academyName: z.string().min(1, 'Academy name is required'),
  academyType: z.enum(['regular_school', 'sports_academy'], {
    required_error: 'Please select academy type',
  }),
  description: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  address: z.string().optional(),
  contactEmail: z.string().email('Invalid email address').optional().or(z.literal('')),
  contactPhone: z.string().optional(),
  adminEmail: z.string().email('Invalid email address'),
  adminPassword: z.string().min(6, 'Password must be at least 6 characters'),
  adminFirstName: z.string().min(1, 'First name is required'),
  adminLastName: z.string().min(1, 'Last name is required'),
});

type AcademyRegistrationFormData = z.infer<typeof academyRegistrationSchema>;

const AcademyRegistrationPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<AcademyRegistrationFormData>({
    resolver: zodResolver(academyRegistrationSchema),
    defaultValues: {
      academyType: 'sports_academy',
    },
  });

  const academyType = watch('academyType');

  const onSubmit = async (data: AcademyRegistrationFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/academies/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          academyName: data.academyName,
          academy_type: data.academyType,
          description: data.description || null,
          country: data.country || null,
          city: data.city || null,
          address: data.address || null,
          contact_email: data.contactEmail || null,
          contact_phone: data.contactPhone || null,
          adminEmail: data.adminEmail,
          adminPassword: data.adminPassword,
          adminFirstName: data.adminFirstName,
          adminLastName: data.adminLastName,
        }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned an error. Please check the console for details.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Registration failed');
      }

      if (result.success) {
        toast.success('Academy registered successfully!');
        
        // Auto-login the academy admin
        try {
          const loginResponse = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: data.adminEmail,
              password: data.adminPassword,
            }),
          });

          const loginResult = await loginResponse.json();
          if (loginResult.success && loginResult.data) {
            console.log('[AcademyRegistration] Login result user:', loginResult.data.user);
            login(loginResult.data.user);
            navigate('/academy-admin/dashboard');
          } else {
            navigate('/login');
          }
        } catch (loginError) {
          console.error('Auto-login failed:', loginError);
          navigate('/login');
        }
      } else {
        throw new Error(result.message || 'Registration failed');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Register Academy
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
            Register your Regular School or Sports Academy
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              Academy Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  {...register('academyName')}
                  label="Academy/School Name *"
                  fullWidth
                  margin="normal"
                  error={!!errors.academyName}
                  helperText={errors.academyName?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" error={!!errors.academyType}>
                  <InputLabel>Academy Type *</InputLabel>
                  <Select
                    {...register('academyType')}
                    label="Academy Type *"
                    defaultValue="sports_academy"
                  >
                    <MenuItem value="regular_school">Regular School (Education and Academy)</MenuItem>
                    <MenuItem value="sports_academy">Sports Academy (Only Sports)</MenuItem>
                  </Select>
                  {errors.academyType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.academyType.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('contactEmail')}
                  label="Academy Contact Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  error={!!errors.contactEmail}
                  helperText={errors.contactEmail?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('contactPhone')}
                  label="Academy Contact Phone"
                  fullWidth
                  margin="normal"
                  error={!!errors.contactPhone}
                  helperText={errors.contactPhone?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('country')}
                  label="Country"
                  fullWidth
                  margin="normal"
                  error={!!errors.country}
                  helperText={errors.country?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('city')}
                  label="City"
                  fullWidth
                  margin="normal"
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('address')}
                  label="Address"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  label="Description"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Academy Admin Account
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              This will be the primary administrator account for your academy. You can add more admins later.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('adminFirstName')}
                  label="Admin First Name *"
                  fullWidth
                  margin="normal"
                  error={!!errors.adminFirstName}
                  helperText={errors.adminFirstName?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('adminLastName')}
                  label="Admin Last Name *"
                  fullWidth
                  margin="normal"
                  error={!!errors.adminLastName}
                  helperText={errors.adminLastName?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('adminEmail')}
                  label="Admin Email *"
                  type="email"
                  fullWidth
                  margin="normal"
                  error={!!errors.adminEmail}
                  helperText={errors.adminEmail?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('adminPassword')}
                  label="Admin Password *"
                  type="password"
                  fullWidth
                  margin="normal"
                  error={!!errors.adminPassword}
                  helperText={errors.adminPassword?.message}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Registering...' : 'Register Academy'}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Already have an account?{' '}
                <Link component={RouterLink} to="/login">
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AcademyRegistrationPage;

